import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { orgAppClient } from '../services/orgAppClient'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import MoroccanLoader from '../components/MoroccanLoader'
import { useOrgAuth } from '../contexts/OrgAuthContext'

export default function AcceptInvitationPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useOrgAuth()

  const isAuthenticated = !!session
  const user = session?.user

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<{email: string; organization_name: string; organization_logo: string | null; user_exists: boolean} | null>(null)

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [accepting, setAccepting] = useState(false)


  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const urlError = params.get('error')

    if (urlError) {
      window.history.replaceState({}, '', location.pathname)
      setFormError(params.get('message') || t('invitation.loginFailed', 'Sign-in failed. Please try again.'))
    }

    const validateToken = async () => {
      try {
        const data = await orgAppClient.getInvitationDetails(token!)
        setInvitation(data)

        // Pre-fill name if user is already authenticated
        if (isAuthenticated && user?.name) {
          setName(user.name)
        }
      } catch (err: unknown) {
        console.error(err); setError(err instanceof Error ? err.message : 'Invalid invitation')
      } finally {
        setLoading(false)
      }
    }
    if (token) {
      validateToken()
    }
  }, [token, isAuthenticated, user, location.pathname, location.search, t])

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    setAccepting(true)
    setFormError(null)
    try {
      const payload: Record<string, string> = { token: token ?? '', name }
      if (!isAuthenticated) {
        payload.password = password
      }

      await orgAppClient.acceptInvitation(payload)

      // On success, redirect to the org dashboard
      navigate('/app')

      // Force reload to update OrgAuthContext if already loaded
      window.location.reload()
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to accept')
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <MoroccanLoader message={t('common.loading', 'Loading...')} />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="max-w-md mx-auto mt-16 p-6 bg-white rounded-lg shadow-sm border border-slate-200">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('invitation.invalidTitle')}</h2>
          <p className="text-slate-600 mb-6">{error || t('invitation.invalidDesc')}</p>
          <Button onClick={() => navigate('/')}>{t('invitation.returnHome')}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto mt-16 p-6 bg-white rounded-lg shadow-sm border border-slate-200">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-slate-900 mb-2">
          {t('invitation.joinTitle', { orgName: invitation.organization_name })}
        </h1>
        <p className="text-slate-600 mb-4 font-medium">
          {isAuthenticated
            ? t('invitation.invitedAs', { email: user?.email || invitation.email })
            : t('invitation.invitedAs', { email: invitation.email })
          }
        </p>
        {!isAuthenticated && (
          <p className="text-sm text-slate-500">
            {invitation.user_exists
              ? t('invitation.explanationTextExistingUser', { email: invitation.email })
              : t('invitation.explanationTextIntro', { email: invitation.email })
            }
          </p>
        )}
      </div>


      {formError && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
          {formError}
        </div>
      )}

      <form onSubmit={handleAccept} className="space-y-5 mb-6">
        <div>
          <label htmlFor="inviteeName" className="block text-sm font-medium text-slate-700 mb-1">
            {t('invitation.fullName')}
          </label>
          <Input
            id="inviteeName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder={isAuthenticated ? user?.name || t('invitation.fullNamePlaceholder') : t('invitation.fullNamePlaceholder')}
          />
        </div>

        {!isAuthenticated && (
          <div>
            <label htmlFor="inviteePassword" className="block text-sm font-medium text-slate-700 mb-1">
              {invitation.user_exists
                ? t('invitation.enterPassword')
                : t('invitation.setPassword')
              }
            </label>
            <Input
              id="inviteePassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>
        )}

        <Button type="submit" className="w-full" loading={accepting}>
          {accepting ? t('invitation.accepting') : t('invitation.accept')}
        </Button>
      </form>

    </div>
  )
}
